import { message } from 'antd'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { LvDetail } from '../components/LvDetail/LvDetail'
import { LvSearchBar } from '../components/LvSearchBar/LvSearchBar'
import { PageLayout } from '../components/PageLayout/PageLayout'
import { PageSkeleton } from '../components/PageSkeleton/PageSkeleton'
import { PropertyDetailFetchError } from '../components/PropertyDetailFetchError/PropertyDetailFetchError'
import { lvDetailPath } from '../utils/paths'
import { useFetchLvDetailData } from '../utils/useFetchLvDetailData'
import { useInitialFetchWatchdog } from '../utils/useFetchWatchdog'

type RouteParams = { katuze?: string; lv?: string }
type SearchParams = { lvCode: string; lvNumber: string }

export const Lv: FunctionComponent = () => {
  const history = useHistory()
  const {
    params: { katuze, lv },
  } = useRouteMatch<RouteParams>()

  useInitialFetchWatchdog()

  const ignoreUrlChange = useRef(false)
  const { fetching, error, data } = useFetchLvDetailData(katuze, lv)
  const { lvData, geometryData } = data

  const handleSearch = useCallback(
    (values: SearchParams) => {
      if (values.lvCode !== katuze || values.lvNumber !== lv) {
        ignoreUrlChange.current = true
        history.push(`${lvDetailPath(values.lvCode, values.lvNumber)}`)
      }
    },
    [history, katuze, lv]
  )

  useEffect(() => {
    if (ignoreUrlChange.current) {
      ignoreUrlChange.current = false
      return
    }

    if (katuze && lv) {
      const isValid = /^\d{1,6}$/.test(katuze) && /^\d{1,6}$/.test(lv)
      if (!isValid) {
        message.error(`${katuze}-${lv} není platný formát LV.`)
      }
    }
  }, [katuze, lv])

  const contentType = useMemo(() => {
    if (fetching) return 'fetching'
    if (!katuze || !lv) return 'initial'
    if (error) return 'notFound'
    return 'found'
  }, [fetching, katuze, lv, error])

  return (
    <>
      <PageLayout title="Vyhledat list vlastnictví" className="!py-0 mb-6">
        <LvSearchBar loading={fetching} onSubmit={handleSearch} />
      </PageLayout>

      {contentType === 'fetching' && <PageSkeleton />}

      {contentType === 'notFound' && (
        <PropertyDetailFetchError
          message={`LV ${katuze} - ${
            lvData?.katastralniUzemi?.nazev || ''
          } (${lv}) nenalezeno`}
          description="Je nám líto, ale dle zadaných údajů se nám nepodařilo najít žádný list vlastnictví."
        />
      )}

      {contentType === 'found' && geometryData && lvData && (
        <LvDetail geometryData={geometryData} lvData={lvData} />
      )}
    </>
  )
}

export default Lv
